import { useI18n, type MessageKey } from '@ecom/i18n';
import { Button, Card } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  buildPublicShopUrl,
  buildShareLink,
  normalizeStorefrontBaseUrl,
  type ShareNetwork,
} from '../share-links';
import { resolveShareState, type ShareState } from '../share-state';

import { BrandIcon } from './brand-icons';

import { SkIcon } from '@/components/ui/SkIcon';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { withUtm } from '@/features/orders/attribution';
import { useProductsQuery } from '@/features/products/api';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { useMyStoreQuery } from '@/features/store/api';
import { env } from '@/lib/env';

const NETWORKS: readonly { readonly network: ShareNetwork; readonly labelKey: MessageKey }[] = [
  { network: 'whatsapp', labelKey: 'merchant.share.whatsapp' },
  { network: 'facebook', labelKey: 'merchant.share.facebook' },
  { network: 'telegram', labelKey: 'merchant.share.telegram' },
];

interface HelpCopy {
  readonly titleKey: MessageKey;
  readonly textKey: MessageKey;
  readonly cta?: { readonly labelKey: MessageKey; readonly to: string };
}

/** The ONE next step for each state in which the shop is not yet public. */
const HELP: Partial<Record<ShareState, HelpCopy>> = {
  'no-store': {
    titleKey: 'merchant.share.noStore.title',
    textKey: 'merchant.share.noStore.text',
    cta: { labelKey: 'merchant.share.noStore.cta', to: '/store' },
  },
  pending: {
    titleKey: 'merchant.share.pending.title',
    textKey: 'merchant.share.pending.text',
  },
  suspended: {
    titleKey: 'merchant.share.suspended.title',
    textKey: 'merchant.share.suspended.text',
  },
  refused: {
    titleKey: 'merchant.share.refused.title',
    textKey: 'merchant.share.refused.text',
  },
  'no-product': {
    titleKey: 'merchant.share.noProduct.title',
    textKey: 'merchant.share.noProduct.text',
    cta: { labelKey: 'merchant.share.noProduct.cta', to: '/products/new' },
  },
  unpublished: {
    titleKey: 'merchant.share.unpublished.title',
    textKey: 'merchant.share.unpublished.text',
    cta: { labelKey: 'merchant.share.unpublished.cta', to: '/products' },
  },
};

/** `execCommand('copy')` on the current selection — deprecated, but the only copy some in-app browsers allow. */
function legacyCopy(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return document.execCommand('copy');
  } catch {
    return false;
  }
}

/** Where merchants usually put a link to their shop (profile « bio », page button…). */
const BIO_LINKS = ['instagram', 'tiktok', 'facebook', 'youtube'] as const;
const BIO_LABELS: Readonly<Record<(typeof BIO_LINKS)[number], string>> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  youtube: 'YouTube',
};

/**
 * « Partager ma boutique » — the public address of the shop, with copy / open / WhatsApp / Facebook / Telegram.
 *
 * Reads only what the dashboard already fetches (the shop, the memberships, the product list): it adds no endpoint. The
 * link appears only when the shop is really public (see `resolveShareState`); until then a single, short help tells the
 * merchant the next step. While anything loads, or if a read fails, it shows NOTHING rather than a wrong prompt.
 *
 * `context="store"` is the permanent home on the shop page, where the form is already right below: it shows the link
 * when ready and stays silent otherwise. `context="dashboard"` also shows the next-step help.
 */
export function ShareStoreCard({
  context,
  part = 'all',
}: {
  readonly context: 'dashboard' | 'store';
  /**
   * `all` (the shop page): the whole card. On the dashboard the two halves live apart (the owner's choice,
   * 2026-10-29): `share` — only the round share buttons, at the top; `link` — the address with copy / open, lower down
   * (and the next-step help while the shop is not public); `panel` — the Figma dashboard's share panel (« Partagez
   * votre boutique »): the title, the address with Copier / Copié, the send and tracked-link buttons together.
   */
  readonly part?: 'all' | 'share' | 'link' | 'panel';
}): ReactElement | null {
  const { t, locale } = useI18n();
  const toast = useToast();
  const shop = useActiveShop();
  const storeQuery = useMyStoreQuery();
  const productsQuery = useProductsQuery();
  const inputRef = useRef<HTMLInputElement>(null);
  // « Copié » shows on the copy button for a moment after a successful copy (the toast says it too).
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 1800);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  const state = resolveShareState({
    store: storeQuery.data,
    storeFailed: storeQuery.isError,
    tenantStatus: shop.activeMembership?.tenantStatus,
    products: productsQuery.data,
    productsFailed: productsQuery.isError,
  });

  if (state === 'loading' || state === 'unavailable') return null;

  if (state !== 'ready') {
    const help = HELP[state];
    if (context === 'store' || part === 'share' || help === undefined) return null;
    return (
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold text-ink">{t(help.titleKey)}</p>
          <p className="text-sm text-inkSoft">{t(help.textKey)}</p>
        </div>
        {help.cta !== undefined ? (
          <Link to={help.cta.to} className="shrink-0">
            <Button variant="secondary">{t(help.cta.labelKey)}</Button>
          </Link>
        ) : null}
      </Card>
    );
  }

  const store = storeQuery.data;
  const url = buildPublicShopUrl(
    normalizeStorefrontBaseUrl(env.storefrontBaseUrl),
    locale,
    store?.slug,
  );
  // The deployment did not say where the storefront lives: no link is better than a wrong one.
  if (store === null || store === undefined || url === null) return null;

  async function copy(link: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(t('merchant.share.copied'));
    } catch {
      // No clipboard permission (or an insecure page): select the link and try the older copy command; if that is
      // refused too, the link stays selected so a manual copy is one gesture.
      inputRef.current?.select();
      if (legacyCopy()) {
        setCopied(true);
        toast.success(t('merchant.share.copied'));
      } else toast.error(t('merchant.share.copyFailed'));
    }
  }

  const message = t('merchant.share.message', { name: store.name });
  const newTab = t('merchant.share.newTab');
  // A round share button in its network's colour (Figma « Souk 2.0 »): icon only, its name as tooltip and accessible
  // name.
  const roundClass = (network: string): string => `sk-social sk-social--${network}`;

  const shareRows = (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-2">
      <div className="flex items-center gap-3">
        <span className="w-[4.5rem] shrink-0 text-xs font-medium text-inkFaint">
          {t('merchant.share.sendRow')}
        </span>
        <div className="flex gap-2">
          {NETWORKS.map(({ network, labelKey }) => (
            <a
              key={network}
              href={buildShareLink(network, withUtm(url, network), message)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t(labelKey)} ${newTab}`}
              title={t(labelKey)}
              className={roundClass(network)}
            >
              <BrandIcon name={network} className="h-[15px] w-[15px]" />
            </a>
          ))}
        </div>
      </div>

      {/* Migration 0097 — tagged links to copy: an order placed after a visit through one of them shows « via … ». */}
      <div className="flex items-center gap-3">
        <span
          className="w-[4.5rem] shrink-0 text-xs font-medium text-inkFaint"
          title={t('merchant.attribution.bioLinksHint')}
        >
          {t('merchant.share.trackedRow')}
        </span>
        <div className="flex gap-2">
          {BIO_LINKS.map((source) => {
            const label = t('merchant.attribution.copyFor', { source: BIO_LABELS[source] });
            return (
              <button
                key={source}
                type="button"
                aria-label={label}
                title={label}
                onClick={() => {
                  void copy(withUtm(url, source, 'social', 'bio'));
                }}
                className={roundClass(source)}
              >
                <BrandIcon name={source} className="h-[15px] w-[15px]" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // The Figma dashboard's share panel.
  if (part === 'panel') {
    return (
      <section className="sk-share" aria-labelledby="share-panel-title">
        <div>
          <span className="sk-eyebrow">{t('merchant.dashboard.shareEyebrow')}</span>
          <h2 id="share-panel-title">{t('merchant.dashboard.shareTitle', { name: store.name })}</h2>
          <p>{t('merchant.dashboard.shareText')}</p>
        </div>
        <div className="sk-share-link">
          <SkIcon name="link" size={16} className="shrink-0" />
          <input
            ref={inputRef}
            type="text"
            readOnly
            dir="ltr"
            value={url}
            aria-label={t('merchant.share.linkLabel')}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t('merchant.share.open')} ${newTab}`}
            title={t('merchant.share.open')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-inkFaint hover:text-ink"
          >
            <SkIcon name="arrow" size={15} className="-rotate-45 rtl:rotate-[-135deg]" />
          </a>
          <button
            type="button"
            className="sk-share-copy"
            onClick={() => {
              void copy(url);
            }}
          >
            {copied ? (
              <>
                <SkIcon name="check" size={14} /> {t('merchant.dashboard.shareCopied')}
              </>
            ) : (
              t('merchant.dashboard.shareCopy')
            )}
          </button>
        </div>
        <div className="sk-share-rows">{shareRows}</div>
      </section>
    );
  }

  // The dashboard's top strip: the share buttons alone, small.
  if (part === 'share') {
    return <Card className="px-5 py-3">{shareRows}</Card>;
  }

  return (
    <Card className="flex flex-col gap-4 p-5" aria-labelledby="share-store-title">
      <div className="flex items-center justify-between gap-3">
        <h2
          id="share-store-title"
          aria-label={t('merchant.share.readyTitle')}
          className="font-display text-lg font-semibold text-ink"
        >
          {t('merchant.share.myShop')}
        </h2>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-goodSoft px-2.5 py-1 text-xs font-semibold text-good">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-good" />
          {t('merchant.share.online')}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center rounded-xl border border-line bg-ground ps-3">
          <input
            ref={inputRef}
            type="text"
            readOnly
            dir="ltr"
            value={url}
            aria-label={t('merchant.share.linkLabel')}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
            className="min-w-0 flex-1 truncate bg-transparent py-2.5 font-mono text-sm text-ink outline-none"
          />
          <button
            type="button"
            aria-label={t('merchant.share.copy')}
            title={t('merchant.share.copy')}
            onClick={() => {
              void copy(url);
            }}
            className="press m-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-onPrimary transition hover:opacity-90"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-[18px] w-[18px]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="11" height="11" rx="2" />
              <path d="M5 15V6a2 2 0 0 1 2-2h9" />
            </svg>
          </button>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${t('merchant.share.open')} ${newTab}`}
          title={t('merchant.share.open')}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-line bg-surface text-ink transition hover:bg-surfaceHover"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-[18px] w-[18px]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 4h6v6" />
            <path d="M20 4 11 13" />
            <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
          </svg>
        </a>
      </div>

      {part === 'all' ? shareRows : null}
    </Card>
  );
}
